import { Popover } from 'antd';
import React, { useEffect, useState } from 'react';
import { NotificationsView } from './notificationsView';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBell, faCircle } from '@fortawesome/free-solid-svg-icons';
import { Button } from '@quyva/shared-ui';
import axios from 'axios';
import { getUrl } from '../../utils/getUrl';
import { Config } from '../../config/envConfig';
import { ProcessType } from '@quyva/common';

const ALERT_SEEN_COUNT_STORAGE_KEY_PREFIX = 'lastSeenAlertCount_';

const getLastSeenAlertCount = (email) => {
    try {
        const stored = localStorage.getItem(
            `${ALERT_SEEN_COUNT_STORAGE_KEY_PREFIX}${email}`
        );
        return stored ? parseInt(stored, 10) : 0;
    } catch {
        return 0;
    }
};

const setLastSeenAlertCount = (email, count) => {
    try {
        localStorage.setItem(
            `${ALERT_SEEN_COUNT_STORAGE_KEY_PREFIX}${email}`,
            String(count)
        );
    } catch {
        // ignore
    }
};

export const NotificationBell = ({
    viewNotifications,
    setViewNotifications,
    newNotification,
    setNewNotification,
    messages,
    socket,
}) => {
    const [showNotifications, setShowNotifications] = useState(false);
    const [hasAlerts, setHasAlerts] = useState(false);
    // Only ALERT notifications belong to the bell; task progress belongs to the
    // Tasks tab. Both read the same newNotification, so each has to filter.
    const hasNewAlert = newNotification?.process === ProcessType.ALERT;

    useEffect(() => {
        if (!newNotification) return;

        // Ensure any previous auto-open flags are cleared; popover will open only on user click
        if (setViewNotifications) {
            setViewNotifications(false);
        }
    }, [newNotification, setViewNotifications]);

    // Check for ALERT notifications. Opening the bell (showNotifications
    // becomes true) counts as reading them: the current alert count is
    // persisted as "seen" so the dot only comes back for alerts that arrive
    // after that point, rather than reappearing for the same history.
    useEffect(() => {
        const checkForAlerts = async () => {
            try {
                const user = JSON.parse(localStorage.getItem('current'));
                if (!user || !user.email || !user.token) {
                    return;
                }

                const dynamicUrl = getUrl(
                    `http://${Config.NODE_SERVER_HOST}:${Config.NODE_SERVER_PORT}/getNotificationHistory?email=${user.email}`
                );

                const encodedToken = encodeURIComponent(user.token);
                const response = await axios.get(dynamicUrl, {
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: encodedToken,
                    },
                });

                if (response && response.data?.notificationHistory) {
                    const alerts = response.data.notificationHistory.filter(
                        (notification) => notification.process === ProcessType.ALERT
                    );

                    if (showNotifications) {
                        setLastSeenAlertCount(user.email, alerts.length);
                        setHasAlerts(false);
                    } else {
                        setHasAlerts(
                            alerts.length > getLastSeenAlertCount(user.email)
                        );
                    }
                }
            } catch (error) {
                console.error('Failed to check for alerts:', error?.message);
            }
        };

        checkForAlerts();
    }, [showNotifications]);

    return (
        <div className='relative mx-3'>
            {(hasAlerts || hasNewAlert) && (
                <div className='absolute -top-1 -right-1 z-10'>
                    <FontAwesomeIcon
                        icon={faCircle}
                        className='text-yellow-400'
                        size='xs'
                    />
                </div>
            )}
            <Popover
                content={
                    <NotificationsView
                        socket={socket}
                        notifications={messages}
                        showNotifications={showNotifications}
                    />
                }
                title={
                    <div className='text-texts-secondary300 font-semibold text-base px-2 py-1'>
                        <FontAwesomeIcon
                            icon={faBell}
                            //color='#4ade80'
                            className='mr-2 text-icons-primary'
                        />
                        Notifications
                    </div>
                }
                trigger='click'
                open={showNotifications}
                onOpenChange={(nextOpen) => {
                    setShowNotifications(nextOpen);
                    if (hasNewAlert) {
                        setNewNotification(null);
                    }
                }}
                placement='bottomLeft'
                fresh={true}
            >
                <Button
                    className={`w-9 h-9`}
                    onClick={() => {
                        if (hasNewAlert) {
                            setNewNotification(null);
                        }
                    }}
                    hoverText={
                        hasAlerts || hasNewAlert
                            ? 'New notifications available'
                            : 'Notifications'
                    }
                    icon={faBell}
                    type={'success'}
                />
            </Popover>
        </div>
    );
};
